import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useEditorState } from '@tiptap/react';
import { ToolbarItemType } from './types.js';
import { useState, useRef, useMemo, useCallback } from 'react';
import { css, styled } from 'styled-components';
import * as RadixDropdown from '@radix-ui/react-dropdown-menu';
import { mediaHover, mediaMobile } from '@fabric-app/ui/constants/styled-utils';
import Tooltip from '@fabric-app/ui/components/Tooltip';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { EditorSelector } from '../../Components/EditorSelector.js';
import { IconChevronDown } from '@fabric-app/icons/svg';
import { isFunction } from '@fabric-app/utils/guards';

const ButtonStyles = css([
  `position:relative;border-radius:10px;border:none;padding:0.313rem;display:flex;align-items:center;justify-content:center;height:1.875rem;min-width:1.875rem;width:min-content;font-size:0.813rem;margin:0;outline:none;font-weight:500;color:var(--fabric-color-text-tertiary);background:none;`,
  `{&:hover{color:var(--fabric-color-text-primary);background:var(--fabric-color-bg-tertiary);}}&:focus{position:relative;color:var(--fabric-color-text-primary);background:var(--fabric-color-bg-tertiary);}&:focus-visible{outline:none;}`,
  `{height:2.25rem;min-width:2.25rem;padding:0.5rem;}&:disabled{opacity:0.5;cursor:not-allowed;}&[data-variant='secondary']{color:`,
  `;background:`,
  `;`,
  `{&:hover{color:`,
  `;background:`,
  `;}}}`
], mediaHover, mediaMobile, cssVar["color-text-primary"], cssVar["color-bg-secondary"], mediaHover, cssVar["color-text-primary"], cssVar["color-bg-tertiary"]);
const ToolbarButtonLabel = styled.span.withConfig({
  displayName: "ToolbarItems__ToolbarButtonLabel",
  componentId: "sc-d6bbb614-0"
})([
  ``
]);
const ToolbarToggleItem = styled.button.withConfig({
  displayName: "ToolbarItems__ToolbarToggleItem",
  componentId: "sc-d6bbb614-1"
})([
  ``,
  ` margin-left:2px;`,
  `{display:none;}&:first-child{margin-left:0;}&[data-state='on']{color:`,
  `;background:`,
  `;}`
], ButtonStyles, ToolbarButtonLabel, cssVar["color-text-primary"], cssVar["color-bg-quarternary"]);
const ToggleSelectorButton = styled(ToolbarToggleItem).withConfig({
  displayName: "ToolbarItems__ToggleSelectorButton",
  componentId: "sc-d6bbb614-2"
})([
  `display:flex;align-items:center;white-space:nowrap;border:1px solid `,
  `;border-radius:8px;gap:4px;`
], cssVar["color-border-primary"]);
const ToolbarButtonEl = styled.button.withConfig({
  displayName: "ToolbarItems__ToolbarButtonEl",
  componentId: "sc-d6bbb614-3"
})([
  ``,
  ` `,
  `{display:none;}`
], ButtonStyles, ToolbarButtonLabel);
const TriggerContent = styled.span.withConfig({
  displayName: "ToolbarItems__TriggerContent",
  componentId: "sc-d6bbb614-4"
})([
  `display:flex;align-items:center;flex-grow:1;gap:12px;`
]);
const ToolbarButtonShortcut = styled.span.withConfig({
  displayName: "ToolbarItems__ToolbarButtonShortcut",
  componentId: "sc-d6bbb614-5"
})([
  `display:flex;justify-content:flex-end;align-items:center;flex-shrink:0;`,
  `{display:none;}`
], mediaMobile);
const ToolbarToggleComponent = ({ editor, item: { label, icon, onClick, getIsActive, shortcut, isHidden }, insideDropdown = false }) => {
  const { isActive } = useEditorState({
    editor,
    selector: (snapshot) => ({
      isActive: getIsActive(snapshot.editor)
    })
  });
  const labelText = typeof label === "function" ? label(editor) : label;
  const renderContent = () => {
    const content = /* @__PURE__ */ jsx(ToolbarToggleItem, {
      "data-state": isActive ? "on" : "off",
      onClick: () => onClick(editor),
      "aria-label": labelText,
      "aria-haspopup": insideDropdown,
      "aria-expanded": insideDropdown && isActive,
      type: "button",
      children: insideDropdown ? /* @__PURE__ */ jsxs(Fragment, {
        children: [
          /* @__PURE__ */ jsxs(TriggerContent, {
            children: [
              icon,
              /* @__PURE__ */ jsx(ToolbarButtonLabel, {
                children: labelText
              })
            ]
          }),
          shortcut && /* @__PURE__ */ jsx(ToolbarButtonShortcut, {
            children: shortcut
          })
        ]
      }) : icon
    });
    if (insideDropdown) {
      return /* @__PURE__ */ jsx(RadixDropdown.DropdownMenuItem, {
        asChild: true,
        children: content
      });
    }
    return /* @__PURE__ */ jsx(Tooltip, {
      label: /* @__PURE__ */ jsxs(Fragment, {
        children: [
          label,
          shortcut
        ]
      }),
      children: content
    });
  };
  if (isHidden?.(editor)) return null;
  return renderContent();
};
const ToolbarButtonComponent = ({ editor, item: { key, label, icon, mobileIcon, onClick, shortcut, isDisabled: isDisabledFn, variant = "default", isHidden }, insideDropdown = false }) => {
  const { isDisabled } = useEditorState({
    editor,
    selector: (snapshot) => ({
      isDisabled: isDisabledFn?.(snapshot.editor)
    })
  });
  const labelText = typeof label === "function" ? label(editor) : label;
  const renderContent = () => {
    if (insideDropdown) {
      return /* @__PURE__ */ jsx(EditorSelector.Item, {
        onClick: () => onClick(editor),
        "aria-label": labelText,
        disabled: isDisabled,
        value: key,
        label: labelText,
        icon,
        mobileIcon
      });
    }
    return /* @__PURE__ */ jsx(Tooltip, {
      label: /* @__PURE__ */ jsxs(Fragment, {
        children: [
          labelText,
          shortcut
        ]
      }),
      children: /* @__PURE__ */ jsx(ToolbarButtonEl, {
        "data-inside-dropdown": insideDropdown,
        onClick: () => onClick(editor),
        "aria-label": labelText,
        "aria-haspopup": insideDropdown,
        type: "button",
        disabled: Boolean(isDisabled),
        "data-variant": variant,
        children: insideDropdown ? /* @__PURE__ */ jsxs(Fragment, {
          children: [
            /* @__PURE__ */ jsxs(TriggerContent, {
              children: [
                icon,
                /* @__PURE__ */ jsx(ToolbarButtonLabel, {
                  children: labelText
                })
              ]
            }),
            shortcut && /* @__PURE__ */ jsx(ToolbarButtonShortcut, {
              children: shortcut
            })
          ]
        }) : icon ? icon : labelText
      })
    });
  };
  if (isHidden?.(editor)) return null;
  return renderContent();
};
const Chevron = styled(IconChevronDown).withConfig({
  displayName: "ToolbarItems__Chevron",
  componentId: "sc-d6bbb614-6"
})([
  `[data-state='on'] &{transform:rotate(180deg);}`
]);
const ToolbarSelectorComponent = ({ editor, item, mobileTarget }) => {
  const [isOpen, setIsOpen] = useState(false);
  const labelText = typeof item.label === "function" ? item.label(editor) : item.label;
  const { selection } = useEditorState({
    editor,
    selector: (snapshot) => ({
      selection: snapshot.editor.state.selection
    }),
    equalityFn(a, b) {
      return a?.selection === b?.selection;
    }
  });
  const editorRef = useRef(editor);
  editorRef.current = editor;
  const value = useMemo(() => {
    if (!selection) return "text";
    return typeof item.value === "function" ? item.value(editorRef.current) : item.value;
  }, [
    selection,
    item
  ]);
  const valueRender = useMemo(() => {
    const option = item.options.find((option2) => option2.value === value);
    if (!option) {
      console.warn(`Option with value ${value} not found in options`, item);
      return null;
    }
    return option.label;
  }, [
    item,
    value
  ]);
  const onSelect = useCallback((value2) => {
    const option = item.options.find((option2) => option2.value === value2);
    if (!option) {
      console.warn(`Option with value ${value2} not found in options`, item);
      return;
    }
    if (option.onSelect) option.onSelect(editor);
    else item.onSelect?.(editor, value2);
    setIsOpen(false);
  }, [
    item,
    editor
  ]);
  if (item.isHidden?.(editor, item)) return null;
  return /* @__PURE__ */ jsx(EditorSelector.Base, {
    label: labelText,
    open: isOpen,
    onOpenChange: setIsOpen,
    mobileTarget,
    renderButton: ({ open, onOpenChange, props }) => /* @__PURE__ */ jsxs(ToggleSelectorButton, {
      "data-state": open ? "on" : "off",
      onClick: (e) => {
        e.preventDefault();
        onOpenChange(!open);
      },
      ...props,
      children: [
        valueRender,
        /* @__PURE__ */ jsx(Chevron, {})
      ]
    }),
    children: /* @__PURE__ */ jsxs(EditorSelector.Group, {
      children: [
        labelText && /* @__PURE__ */ jsxs(EditorSelector.GroupTitle, {
          children: [
            labelText,
            ":"
          ]
        }),
        item.options.map((option) => {
          return /* @__PURE__ */ jsx(EditorSelector.Item, {
            value: option.value,
            onClick: () => onSelect(option.value),
            label: option.label,
            icon: option.icon,
            mobileIcon: option.mobileIcon
          }, option.value);
        })
      ]
    })
  });
};
const ToolbarDropdownComponent = ({ editor, item: { icon, label, items } }) => {
  const labelText = typeof label === "function" ? label(editor) : label;
  return /* @__PURE__ */ jsx(EditorSelector.Base, {
    label: labelText,
    renderButton: ({ open, onOpenChange, props }) => /* @__PURE__ */ jsxs(ToggleSelectorButton, {
      "data-state": open ? "on" : "off",
      onClick: (e) => {
        e.preventDefault();
        onOpenChange(!open);
      },
      ...props,
      children: [
        icon,
        " ",
        labelText,
        /* @__PURE__ */ jsx(Chevron, {})
      ]
    }),
    children: renderDropdownContent(editor, items)
  });
};
const renderDropdownContent = (editor, items) => {
  return items.map((item) => {
    const label = isFunction(item.label) ? item.label(editor) : item.label;
    return /* @__PURE__ */ jsxs(EditorSelector.Group, {
      children: [
        label && /* @__PURE__ */ jsx(EditorSelector.GroupTitle, {
          children: label
        }),
        renderDropdownGroupContent(editor, item.items)
      ]
    }, `dropdown-group-${item.name}`);
  });
};
const renderDropdownGroupContent = (editor, items) => {
  return items.map((item) => {
    switch (item.type) {
      case ToolbarItemType.Toggle:
        return /* @__PURE__ */ jsx(ToolbarToggleComponent, {
          editor,
          item,
          insideDropdown: true
        }, `dropdown-item-${item.key}`);
      case ToolbarItemType.Button:
        return /* @__PURE__ */ jsx(ToolbarButtonComponent, {
          editor,
          item,
          insideDropdown: true
        }, `dropdown-item-${item.key}`);
      case ToolbarItemType.Custom:
        return item.render(`dropdown-item-${item.key}`, editor, true);
    }
  });
};

export { ToolbarButtonComponent, ToolbarDropdownComponent, ToolbarSelectorComponent, ToolbarToggleComponent, ToolbarToggleItem };
//# sourceMappingURL=ToolbarItems.js.map
